import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { HardDrive, Play, Loader2, CheckCircle2, XCircle, Calendar, FolderOpen, RefreshCw, AlertTriangle, Lock, ShieldCheck, ShieldAlert, Trash2, Archive, RotateCcw, Upload, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";

interface DriveStatus {
  connected: boolean;
  email: string | null;
}

interface BackupRecord {
  id: string;
  createdAt: string;
  status: "pending" | "in_progress" | "completed" | "failed";
  kind?: "platform" | "workspace";
  workspaceId?: string | null;
  workspaceName?: string | null;
  workspaceCount: number;
  totalRecords: number;
  driveFolderId: string | null;
  driveFolderUrl: string | null;
  errorMessage?: string | null;
  encryptionStatus?: "encrypted" | "plaintext_legacy";
  backupSchemaVersion?: number;
}

interface RetentionPolicy { weeks: number; months: number }

interface PruneOutcome {
  startedAt: string;
  finishedAt: string;
  policy: RetentionPolicy;
  inspected: number;
  kept: number;
  pruned: number;
  driveDeletes: number;
  errors: string[];
  triggerType?: string;
}

interface LastPrune {
  action: "backup_pruned" | "backup_prune_failed";
  at: string;
  outcome: PruneOutcome | null;
}

interface BackupData {
  driveStatus: DriveStatus;
  backups: BackupRecord[];
  lastBackup: BackupRecord | null;
  retention: RetentionPolicy;
  lastPrune: LastPrune | null;
}

interface RestoreTest {
  id: string;
  backupId: string | null;
  status: "pending" | "in_progress" | "passed" | "failed" | "skipped";
  triggerType: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  assertionsJson: string | null;
  errorMessage: string | null;
}

interface RestoreTestData {
  tests: RestoreTest[];
  backupKekConfigured: boolean;
  scratchDbConfigured: boolean;
}

const RESTORE_TEST_WINDOW_MONTHS = 12;

interface AssertionShape { name: string; ok: boolean; detail?: string }

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

function StatusIcon({ status }: { status: BackupRecord["status"] | RestoreTest["status"] }) {
  if (status === "completed" || status === "passed") return <CheckCircle2 className="h-5 w-5 text-green-500" />;
  if (status === "failed") return <XCircle className="h-5 w-5 text-red-500" />;
  if (status === "skipped") return <AlertTriangle className="h-5 w-5 text-amber-500" />;
  return <Loader2 className="h-5 w-5 animate-spin" />;
}

function BackupHistorySection({
  title,
  description,
  backups,
  emptyMessage,
  showWorkspaceTag = false,
}: {
  title: string;
  description: string;
  backups: BackupRecord[];
  emptyMessage: string;
  showWorkspaceTag?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {backups.length > 0 ? (
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {backups.map((backup) => (
                <div key={backup.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`backup-${backup.id}`}>
                  <div className="flex items-center gap-3">
                    <StatusIcon status={backup.status} />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{formatDate(backup.createdAt)}</p>
                        {showWorkspaceTag && (
                          <Badge variant="outline" className="text-xs" data-testid={`backup-workspace-tag-${backup.id}`}>
                            {backup.workspaceName ?? backup.workspaceId ?? "unknown"}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{backup.workspaceCount} workspaces, {backup.totalRecords} records</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {backup.encryptionStatus === "encrypted" ? (
                      <Badge variant="outline" className="gap-1"><Lock className="h-3 w-3" /> enc</Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1"><ShieldAlert className="h-3 w-3" /> legacy</Badge>
                    )}
                    <Badge variant={backup.status === "completed" ? "default" : backup.status === "failed" ? "destructive" : "secondary"}>{backup.status}</Badge>
                    {backup.driveFolderUrl && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={backup.driveFolderUrl} target="_blank" rel="noopener noreferrer">
                          <FolderOpen className="w-4 h-4 mr-1" /> View in Drive
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="text-center py-8 text-muted-foreground" data-testid={`backup-section-empty-${title.toLowerCase().replace(/\s+/g, "-")}`}>{emptyMessage}</div>
        )}
      </CardContent>
    </Card>
  );
}

function BackupsTab() {
  const { toast } = useToast();
  const { data, isLoading, refetch } = useQuery<BackupData>({ queryKey: ["/api/platform/backups"] });
  // Synchronous guards — prevent a rapid double-click from calling mutate()
  // twice before React re-renders with mutation.isPending = true.
  const isBackingUpRef = useRef(false);
  const isPruningRef = useRef(false);

  const handleBackup = () => {
    if (isBackingUpRef.current) return;
    isBackingUpRef.current = true;
    backupMutation.mutate(undefined, { onSettled: () => { isBackingUpRef.current = false; } });
  };

  const handlePrune = () => {
    if (isPruningRef.current) return;
    isPruningRef.current = true;
    pruneMutation.mutate(undefined, { onSettled: () => { isPruningRef.current = false; } });
  };

  const backupMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/platform/backups/create");
    },
    onSuccess: (resp: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/backups"] });
      toast({
        title: "Backup completed",
        description: `Successfully backed up ${resp.workspaceCount} workspaces with ${resp.totalRecords} records.`,
      });
    },
    onError: (error: any) => {
      toast({ title: "Backup failed", description: error.message || "Failed to create backup", variant: "destructive" });
    },
  });

  // Task #386 — connect/disconnect Clarik's OWN backup Drive identity
  // (a single clarik.io Google account, drive.file scope). Replaces the old
  // "set up in Replit Integrations" connector flow.
  const driveConnectMutation = useMutation({
    mutationFn: async () => {
      const returnUrl = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/platform/backups";
      const res = await apiRequest("GET", `/api/platform/backups/drive/auth?returnUrl=${encodeURIComponent(returnUrl)}`);
      return res.json();
    },
    onSuccess: (data: { authUrl: string }) => { window.location.href = data.authUrl; },
    onError: (error: any) => toast({ title: "Error", description: error?.message || "Failed to start Drive connect", variant: "destructive" }),
  });

  const driveDisconnectMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/backups/drive/disconnect")).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/backups"] });
      toast({ title: "Disconnected", description: "Backup Google Drive disconnected." });
    },
    onError: (error: any) => toast({ title: "Error", description: error?.message || "Failed to disconnect Drive", variant: "destructive" }),
  });

  const pruneMutation = useMutation({
    mutationFn: async (): Promise<PruneOutcome> => {
      const res = await apiRequest("POST", "/api/platform/backups/prune");
      return (await res.json()) as PruneOutcome;
    },
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/backups"] });
      const failed = (resp?.errors?.length ?? 0) > 0;
      toast({
        title: failed ? "Prune completed with errors" : "Prune completed",
        description: `Pruned ${resp.pruned} of ${resp.inspected} (kept ${resp.kept}, drive deletes ${resp.driveDeletes}).`,
        variant: failed ? "destructive" : "default",
      });
    },
    onError: (error: any) => {
      toast({ title: "Prune failed", description: error.message || "Failed to prune backups", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh-backups">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Google Drive Connection</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {data?.driveStatus?.connected ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-500" /><span className="font-medium">Connected</span></div>
                <p className="text-sm text-muted-foreground" data-testid="text-drive-email">{data.driveStatus.email}</p>
                <Button
                  variant="outline" size="sm"
                  onClick={() => driveDisconnectMutation.mutate()}
                  disabled={driveDisconnectMutation.isPending}
                  data-testid="button-disconnect-drive"
                >
                  {driveDisconnectMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2"><XCircle className="h-5 w-5 text-muted-foreground" /><span className="font-medium text-muted-foreground">Not Connected</span></div>
                <p className="text-sm text-muted-foreground">Connect a Clarik Google account (clarik.io) to enable encrypted backups.</p>
                <Button
                  size="sm"
                  onClick={() => driveConnectMutation.mutate()}
                  disabled={driveConnectMutation.isPending}
                  data-testid="button-connect-drive"
                >
                  {driveConnectMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Connect Google Drive
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Backup</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {data?.lastBackup ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <StatusIcon status={data.lastBackup.status} />
                  <span className="font-medium">{formatDate(data.lastBackup.createdAt)}</span>
                </div>
                <p className="text-sm text-muted-foreground">{data.lastBackup.workspaceCount} workspaces, {data.lastBackup.totalRecords} records</p>
                {data.lastBackup.encryptionStatus === "encrypted" ? (
                  <Badge variant="outline" className="gap-1" data-testid="badge-encryption-encrypted">
                    <Lock className="h-3 w-3" /> Encrypted at rest
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1" data-testid="badge-encryption-legacy">
                    <ShieldAlert className="h-3 w-3" /> Plaintext (legacy)
                  </Badge>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground">No backups yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Retention Policy</CardTitle>
              <CardDescription>
                Keeps the {data?.retention?.weeks ?? 12} most-recent successful backups (regardless
                of age) PLUS every backup created in the last {data?.retention?.weeks ?? 12} weeks PLUS
                the earliest successful backup of each of the last {data?.retention?.months ?? 12}
                calendar months.
                Older backups are pruned automatically after every successful run.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={handlePrune}
              disabled={pruneMutation.isPending}
              data-testid="button-prune-backups"
            >
              {pruneMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Pruning…</> : <><Trash2 className="w-4 h-4 mr-2" />Prune Now</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline" className="gap-1"><Archive className="h-3 w-3" /> {data?.retention?.weeks ?? 12} weekly</Badge>
            <Badge variant="outline" className="gap-1"><Calendar className="h-3 w-3" /> {data?.retention?.months ?? 12} monthly anchors</Badge>
            {data?.lastPrune ? (
              <span className="text-muted-foreground" data-testid="text-last-prune">
                Last sweep {formatDate(data.lastPrune.at)} —{" "}
                {data.lastPrune.action === "backup_pruned" ? (
                  <span className="text-green-600 dark:text-green-400">
                    pruned {data.lastPrune.outcome?.pruned ?? 0} of {data.lastPrune.outcome?.inspected ?? 0} (kept {data.lastPrune.outcome?.kept ?? 0})
                  </span>
                ) : (
                  <span className="text-red-600 dark:text-red-400">
                    failed — {data.lastPrune.outcome?.errors?.[0] ?? "see audit log"}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">No prune sweeps yet.</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Create Backup</CardTitle>
              <CardDescription>All workspace data is envelope-encrypted with BACKUP_KEK before upload.</CardDescription>
            </div>
            <Button onClick={handleBackup} disabled={!data?.driveStatus?.connected || backupMutation.isPending} data-testid="button-create-backup">
              {backupMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Backing up...</> : <><Play className="w-4 h-4 mr-2" />Backup Now</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!data?.driveStatus?.connected && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-amber-800 dark:text-amber-200">Connect Google Drive to enable platform backups</span>
            </div>
          )}
        </CardContent>
      </Card>

      <BackupHistorySection
        title="Platform Backups"
        description="Full multi-tenant bundle backups stored in Google Drive"
        backups={(data?.backups ?? []).filter((b) => (b.kind ?? "platform") === "platform")}
        emptyMessage="No platform backups yet. Create your first backup above."
      />
      <BackupHistorySection
        title="Workspace Backups"
        description="Per-workspace standalone backups stored in Google Drive"
        backups={(data?.backups ?? []).filter((b) => b.kind === "workspace")}
        emptyMessage="No workspace backups yet."
        showWorkspaceTag
      />
    </div>
  );
}

function RestoreTestsTab() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<RestoreTestData>({
    queryKey: [`/api/platform/backup-restore-tests?months=${RESTORE_TEST_WINDOW_MONTHS}`],
  });
  // Synchronous guard — prevents a rapid double-click from calling mutate()
  // twice before React re-renders with runMutation.isPending = true.
  const isRunningRef = useRef(false);

  const handleRunTest = () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    runMutation.mutate(undefined, { onSettled: () => { isRunningRef.current = false; } });
  };

  const runMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/platform/backup-restore-tests/run");
    },
    onSuccess: (resp: any) => {
      queryClient.invalidateQueries({ queryKey: [`/api/platform/backup-restore-tests?months=${RESTORE_TEST_WINDOW_MONTHS}`] });
      toast({
        title: `Restore test ${resp.status}`,
        description: resp.errorMessage || `Ran ${resp.assertions?.length ?? 0} assertions in ${resp.durationMs}ms`,
        variant: resp.status === "failed" ? "destructive" : "default",
      });
    },
    onError: (error: any) => {
      toast({ title: "Restore test failed to start", description: error.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Encryption Key</CardTitle>
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {data?.backupKekConfigured ? (
              <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-500" /><span className="font-medium">BACKUP_KEK configured</span></div>
            ) : (
              <div className="flex items-center gap-2"><XCircle className="h-5 w-5 text-red-500" /><span className="font-medium">BACKUP_KEK missing — backups disabled</span></div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scratch DB</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {data?.scratchDbConfigured ? (
              <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-500" /><span className="font-medium">Scratch DB configured</span></div>
            ) : (
              <div className="flex items-center gap-2"><XCircle className="h-5 w-5 text-red-500" /><span className="text-sm text-muted-foreground">Required — set BACKUP_RESTORE_TEST_SCRATCH_DB_URL or restore tests will fail.</span></div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Restore Test</CardTitle>
              <CardDescription>Decrypts the latest backup and replays it into the scratch Postgres. Runs monthly automatically.</CardDescription>
            </div>
            <Button onClick={handleRunTest} disabled={runMutation.isPending} data-testid="button-run-restore-test">
              {runMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running...</> : <><Play className="w-4 h-4 mr-2" />Run Now</>}
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Restore Test History</CardTitle>
          <CardDescription>Last {RESTORE_TEST_WINDOW_MONTHS} months (scheduled + manual)</CardDescription>
        </CardHeader>
        <CardContent>
          {data?.tests && data.tests.length > 0 ? (
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {data.tests.map((t) => {
                  let assertions: AssertionShape[] = [];
                  if (t.assertionsJson) {
                    try { assertions = JSON.parse(t.assertionsJson); } catch { /* malformed — render as none */ }
                  }
                  const failed = assertions.filter((a) => !a.ok);
                  return (
                    <div key={t.id} className="p-3 rounded-lg border space-y-2" data-testid={`restore-test-${t.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <StatusIcon status={t.status} />
                          <div>
                            <p className="font-medium">{formatDate(t.startedAt)}</p>
                            <p className="text-sm text-muted-foreground">
                              {t.triggerType} · {t.durationMs ? `${t.durationMs}ms` : "—"} · {assertions.length} assertions
                            </p>
                          </div>
                        </div>
                        <Badge variant={t.status === "passed" ? "default" : t.status === "failed" ? "destructive" : "secondary"}>{t.status}</Badge>
                      </div>
                      {t.errorMessage && (
                        <p className="text-sm text-red-600 dark:text-red-400" data-testid={`restore-test-error-${t.id}`}>{t.errorMessage}</p>
                      )}
                      {failed.length > 0 && (
                        <ul className="text-sm space-y-1 list-disc list-inside text-red-700 dark:text-red-300">
                          {failed.map((a) => (
                            <li key={a.name}><span className="font-mono">{a.name}</span>{a.detail ? ` — ${a.detail}` : ""}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-muted-foreground">No restore tests yet. Run one above or wait for the monthly tick.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface WorkspaceOption {
  id: string;
  name: string;
  slug: string;
}

interface BackupManifest {
  workspace?: { id?: string; name?: string; slug?: string };
  capturedAt?: string;
  capturedBy?: string;
  rowCount?: number;
}

async function readManifestFromZip(file: File): Promise<BackupManifest | null> {
  try {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const TARGET_NAMES = new Set(["manifest.json", "_manifest.json"]);

    for (let i = 0; i < bytes.length - 30; i++) {
      if (bytes[i] !== 0x50 || bytes[i + 1] !== 0x4b || bytes[i + 2] !== 0x03 || bytes[i + 3] !== 0x04) continue;
      const fileNameLen = bytes[i + 26] | (bytes[i + 27] << 8);
      const extraLen = bytes[i + 28] | (bytes[i + 29] << 8);
      if (i + 30 + fileNameLen > bytes.length) continue;
      const entryName = new TextDecoder().decode(bytes.slice(i + 30, i + 30 + fileNameLen));
      const baseName = entryName.split("/").pop() ?? entryName;
      if (!TARGET_NAMES.has(baseName)) continue;

      const compression = bytes[i + 8] | (bytes[i + 9] << 8);
      const compSize = bytes[i + 18] | (bytes[i + 19] << 8) | (bytes[i + 20] << 16) | (bytes[i + 21] << 24);
      const dataStart = i + 30 + fileNameLen + extraLen;
      if (dataStart + compSize > bytes.length) continue;
      const compData = bytes.slice(dataStart, dataStart + compSize);

      let jsonBytes: Uint8Array;
      if (compression === 0) {
        jsonBytes = compData;
      } else if (compression === 8) {
        const ds = new DecompressionStream("deflate-raw");
        const writer = ds.writable.getWriter();
        writer.write(compData);
        writer.close();
        const reader = ds.readable.getReader();
        const chunks: Uint8Array[] = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
        }
        const total = chunks.reduce((acc, c) => acc + c.length, 0);
        jsonBytes = new Uint8Array(total);
        let off = 0;
        for (const c of chunks) { jsonBytes.set(c, off); off += c.length; }
      } else {
        continue;
      }

      return JSON.parse(new TextDecoder().decode(jsonBytes)) as BackupManifest;
    }
    return null;
  } catch {
    return null;
  }
}

function SelectiveRestoreTab() {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [workspaceId, setWorkspaceId] = useState("");
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const [confirm, setConfirm] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<any>(null);
  const [manifest, setManifest] = useState<BackupManifest | null | undefined>(undefined);
  const [manifestLoading, setManifestLoading] = useState(false);

  const { data: wsData } = useQuery<{ workspaces: WorkspaceOption[] }>({
    queryKey: ["/api/platform/workspaces"],
  });

  const expectedConfirm = mode === "replace" ? "REPLACE-WORKSPACE" : "MERGE-RESTORE";
  const canSubmit = !!file && !!workspaceId && confirm === expectedConfirm;

  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("No file selected");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("workspaceId", workspaceId);
      fd.append("mode", mode);
      fd.append("confirm", confirm);
      const res = await fetch("/api/platform/restore-workspace-surgical", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Restore failed");
      return data;
    },
    onSuccess: (data) => {
      setResult(data);
      toast({ title: "Surgical restore complete", description: data.message });
    },
    onError: (error: any) => {
      toast({ title: "Restore failed", description: error.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-blue-600" />
            Selective Workspace Restore
          </CardTitle>
          <CardDescription>
            Restore a single workspace from a backup zip without touching any other workspace.
            Upload the backup zip you downloaded from Google Drive, pick the workspace, and choose a mode.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Workspace</label>
              <Select value={workspaceId} onValueChange={setWorkspaceId}>
                <SelectTrigger data-testid="select-restore-workspace">
                  <SelectValue placeholder="Select workspace…" />
                </SelectTrigger>
                <SelectContent>
                  {(wsData?.workspaces ?? []).map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name} <span className="text-muted-foreground text-xs ml-1">({w.slug})</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Choose the workspace to restore into.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Mode</label>
              <Select value={mode} onValueChange={(v) => { setMode(v as "merge" | "replace"); setConfirm(""); }}>
                <SelectTrigger data-testid="select-restore-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Merge — add missing rows only (safe)</SelectItem>
                  <SelectItem value="replace">Replace — wipe this workspace then re-insert</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {mode === "merge"
                  ? "Inserts rows from the backup that don't exist in the DB. Nothing is overwritten."
                  : "Deletes all data for this workspace, then re-inserts from the backup. Other workspaces are untouched."}
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Backup zip</label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                data-testid="button-choose-restore-file"
              >
                <Upload className="h-4 w-4 mr-2" />
                {file ? "Change file" : "Choose file"}
              </Button>
              {file && <span className="text-sm text-muted-foreground">{file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</span>}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".zip"
              className="hidden"
              data-testid="input-restore-file"
              onChange={async (e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                setManifest(undefined);
                if (!f) return;
                setManifestLoading(true);
                const m = await readManifestFromZip(f);
                setManifest(m);
                setManifestLoading(false);
              }}
            />
            <p className="text-xs text-muted-foreground">Download the backup zip from Google Drive and upload it here.</p>

            {file && (
              <div className={`mt-2 flex items-start gap-2 p-3 rounded-lg border text-sm ${
                manifestLoading
                  ? "bg-muted/40 border-border"
                  : manifest?.workspace?.name
                    ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
                    : manifest === null
                      ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800"
                      : "bg-muted/40 border-border"
              }`} data-testid="backup-manifest-info">
                {manifestLoading ? (
                  <><Loader2 className="h-4 w-4 animate-spin shrink-0 mt-0.5 text-muted-foreground" /><span className="text-muted-foreground">Reading backup manifest…</span></>
                ) : manifest?.workspace?.name ? (
                  <>
                    <Info className="h-4 w-4 shrink-0 mt-0.5 text-blue-600" />
                    <div>
                      <span className="font-medium text-blue-900 dark:text-blue-200">Backup covers: </span>
                      <span className="font-semibold text-blue-900 dark:text-blue-100" data-testid="manifest-workspace-name">{manifest.workspace.name}</span>
                      {manifest.workspace.slug && (
                        <span className="text-blue-700 dark:text-blue-300 text-xs ml-1">({manifest.workspace.slug})</span>
                      )}
                      {manifest.capturedAt && (
                        <span className="text-blue-700 dark:text-blue-300 ml-2">
                          · captured {new Date(manifest.capturedAt).toLocaleString()}
                        </span>
                      )}
                      {manifest.rowCount != null && (
                        <span className="text-blue-700 dark:text-blue-300 ml-2">· {manifest.rowCount.toLocaleString()} rows</span>
                      )}
                    </div>
                  </>
                ) : manifest === null ? (
                  <>
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                    <span className="text-amber-800 dark:text-amber-300">No manifest found in this zip — verify you have the correct backup file before restoring.</span>
                  </>
                ) : null}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Confirm</label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={`Type "${expectedConfirm}" to proceed`}
              data-testid="input-restore-confirm"
              className={confirm && confirm !== expectedConfirm ? "border-red-500" : ""}
            />
            <p className="text-xs text-muted-foreground">
              Type <span className="font-mono font-medium">{expectedConfirm}</span> exactly to enable the restore button.
            </p>
          </div>

          {mode === "replace" && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <strong>Replace mode</strong> deletes all current data for this workspace (leads, clients, invoices, settings)
                and replaces it with the backup. This cannot be undone. All other workspaces are unaffected.
              </p>
            </div>
          )}

          <Button
            onClick={() => restoreMutation.mutate()}
            disabled={!canSubmit || restoreMutation.isPending}
            variant={mode === "replace" ? "destructive" : "default"}
            data-testid="button-run-surgical-restore"
          >
            {restoreMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Restoring…</>
            ) : (
              <><RotateCcw className="h-4 w-4 mr-2" /> Run {mode === "merge" ? "Merge" : "Replace"} Restore</>
            )}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" /> Restore Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm">{result.message}</p>
            {result.warnings?.length > 0 && (
              <div className="text-xs text-amber-600">
                {result.warnings.map((w: string, i: number) => <p key={i}>{w}</p>)}
              </div>
            )}
            <ScrollArea className="h-48">
              <table className="text-xs w-full">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="text-left pb-1">Table</th>
                    <th className="text-right pb-1">Inserted</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.perTable ?? []).map((t: any) => (
                    <tr key={t.table} className="border-b border-border/30">
                      <td className="font-mono py-0.5">{t.table}</td>
                      <td className="text-right py-0.5">{t.inserted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function BackupsContent() {
  return (
    <>
      <div className="p-3 sm:p-6 space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Platform Backups</h1>
          <p className="text-muted-foreground">Encrypted backups to Google Drive plus monthly automated restore tests.</p>
        </div>
        <Tabs defaultValue="backups">
          <TabsList>
            <TabsTrigger value="backups" data-testid="tab-backups">Backups</TabsTrigger>
            <TabsTrigger value="restore-tests" data-testid="tab-restore-tests">Restore Tests</TabsTrigger>
            <TabsTrigger value="selective-restore" data-testid="tab-selective-restore">Selective Restore</TabsTrigger>
          </TabsList>
          <TabsContent value="backups" className="mt-6"><BackupsTab /></TabsContent>
          <TabsContent value="restore-tests" className="mt-6"><RestoreTestsTab /></TabsContent>
          <TabsContent value="selective-restore" className="mt-6"><SelectiveRestoreTab /></TabsContent>
        </Tabs>
      </div>
    </>
  );
}

export default function PlatformBackups() {
  return <PlatformLayout><BackupsContent /></PlatformLayout>;
}
